import React, { createContext, useContext, useMemo } from 'react'

interface TeamSlugContextType {
	teamSlug: string | null
	isPortal: boolean
}

const TeamSlugContext = createContext<TeamSlugContextType | undefined>(
	undefined
)

export const TeamSlugProvider = ({
	children,
}: {
	children: React.ReactNode
}) => {
	const { teamSlug, isPortal } = useMemo(() => {
		const hostname = window.location.hostname
		const parts = hostname.split('.')

		// 1. Localhost handling (e.g., acme.localhost or acme.127.0.0.1.nip.io)
		if (hostname.includes('localhost')) {
			if (parts.length > 1 && parts[0] !== 'www') {
				return { teamSlug: parts[0], isPortal: true }
			}
			return { teamSlug: null, isPortal: false }
		}

		// 2. Exclude Vercel preview domains (*.vercel.app)
		if (hostname.endsWith('.vercel.app')) {
			return { teamSlug: null, isPortal: false }
		}

		// 3. Generic wildcard-domain handling (prod, staging, preview, etc.)
		//    If the host has at least 3 segments (sub + domain + tld) and the first segment
		//    is not a reserved word (www/app), treat the first segment as the team slug.
		if (parts.length >= 3) {
			const sub = parts[0]
			if (sub !== 'www' && sub !== 'app') {
				return { teamSlug: sub, isPortal: true }
			}
		}

		return { teamSlug: null, isPortal: false }
	}, [])

	return (
		<TeamSlugContext.Provider value={{ teamSlug, isPortal }}>
			{children}
		</TeamSlugContext.Provider>
	)
}

export const useTeamSlug = () => {
	const context = useContext(TeamSlugContext)
	if (context === undefined) {
		throw new Error('useTeamSlug must be used within a TeamSlugProvider')
	}
	return context
}
